import React from 'react';
import { Box, Typography, CircularProgress, Fade } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

const GlobeLoader = ({
    loading,
    loadingProgress,
    dataLoading,
    textureLoading
}) => {
    const theme = useTheme();
    return (
        <Fade in={loading} timeout={800}>
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    bgcolor: '#000',
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(8px)'
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 4
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            width: 180,
                            height: 180,
                            mb: 4
                        }}
                    >
                        <CircularProgress
                            variant="determinate"
                            value={loadingProgress}
                            size={180}
                            thickness={1.2}
                            sx={{
                                color: theme.palette.primary.main,
                                position: 'absolute',
                                zIndex: 2
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}
                        >
                            <Box
                                component="img"
                                src="./world.jpg"
                                sx={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: '50%',
                                    animation: 'pulse 2s infinite',
                                    '@keyframes pulse': {
                                        '0%': { transform: 'scale(0.95)', opacity: 0.8 },
                                        '50%': { transform: 'scale(1)', opacity: 1 },
                                        '100%': { transform: 'scale(0.95)', opacity: 0.8 }
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            fontWeight: 600,
                            color: '#fff',
                            mb: 2,
                            textAlign: 'center'
                        }}
                    >
                        {dataLoading ? 'Loading Globe Data' : 'Loading 3D Assets'}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: alpha('#fff', 0.7),
                            textAlign: 'center',
                            maxWidth: 400
                        }}
                    >
                        {dataLoading
                            ? 'Fetching location data from server...'
                            : textureLoading
                                ? 'Preparing the interactive 3D world map...'
                                : 'Initializing globe visualization...'}
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                            <CircularProgress
                                variant="determinate"
                                value={dataLoading ? 100 : loadingProgress}
                                size={38}
                                thickness={4}
                                sx={{
                                    color: dataLoading
                                        ? theme.palette.info.main
                                        : theme.palette.primary.main
                                }}
                            />
                            <Box
                                sx={{
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    position: 'absolute',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {dataLoading ? (
                                    <Box
                                        component="span"
                                        sx={{
                                            animation: 'pulse 1.5s infinite',
                                            '@keyframes pulse': {
                                                '0%': { opacity: 0.5 },
                                                '50%': { opacity: 1 },
                                                '100%': { opacity: 0.5 },
                                            },
                                            height: 6,
                                            width: 6,
                                            borderRadius: '50%',
                                            bgcolor: theme.palette.info.main
                                        }}
                                    />
                                ) : (
                                    <Typography
                                        variant="caption"
                                        component="div"
                                        sx={{ fontWeight: 600, fontSize: '0.6rem' }}
                                    >
                                        {loadingProgress}%
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 600
                            }}
                        >
                            {'Connecting to server...'
                            }
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Fade>
    );
};

export default GlobeLoader;
